/** Load in a table cell (DESIGN §16.5, §16.6): the last full week's number, eight weeks as a line, the verdict. */
import { useModel } from "../context.js";
import { loadKey, loadLoad, useResource } from "../data.js";
import { mondayOf, monthDay } from "../model/days.js";
import { type LoadData, type LoadStatus, loadNumber, loadStatus, trendText } from "../model/load.js";
import { Sparkline, Status } from "../../kit/index.js";

export function useLoad(): LoadData | undefined {
  return useResource(loadKey, loadLoad).value;
}

export interface AthleteLoad {
  status: LoadStatus;
  /** Monday of the week read: the last full one while this week runs. */
  week: string;
  recent: number[];
}

/** An athlete's load for `week` (default this week), read on the last full week while it runs. */
export function athleteLoad(data: LoadData | undefined, id: string, today: string, week?: string): AthleteLoad | null {
  const athlete = data?.athletes.find((a) => a.id === id);
  if (!data || !athlete) return null;
  let index = week ? data.weeks.indexOf(week) : data.weeks.length - 1;
  if (index < 0) return null;
  if (data.weeks[index] === mondayOf(today) && index > 0) index -= 1;
  return { status: loadStatus(data.weeks, athlete, index), week: data.weeks[index]!, recent: athlete.loads.slice(Math.max(0, index - 7), index + 1) };
}

/** For sorting a Load column: the number, or −1 when there is none. */
export function loadValue(load: AthleteLoad | null): number {
  return load && load.status.trend !== "none" ? load.status.value : -1;
}

export function LoadCell({ load }: { load: AthleteLoad | null }) {
  if (!load || load.status.trend === "none") return <span className="k-muted">—</span>;
  const { status } = load;
  const mark = status.trend === "high" ? "high" : status.trend === "low" ? "low" : null;
  return (
    <span className="k-load" title={`Week of ${monthDay(load.week)}: ${trendText(status)}`}>
      <span className="num k-load-value">{loadNumber(status.value)}</span>
      <Sparkline values={load.recent} width={56} height={18} />
      {mark ? <Status kind={mark} /> : <span className="sr">{trendText(status)}</span>}
    </span>
  );
}

export function useModelToday(): string {
  return useModel().today;
}
